// Shared helpers for the Wallet & Analytics dashboard area.
//
// Money is stored as integer cents in its originating currency (ISO 4217).
// Amounts must be displayed in their original currency WITHOUT any currency
// conversion (Requirements 6.4 / 8.4).

export type Period = 'day' | 'week' | 'month' | 'custom_range'

export interface TimeFilterValue {
  period: Period
  // RFC3339 timestamps, only meaningful for custom_range
  start?: string
  end?: string
}

export const DEFAULT_TIME_FILTER: TimeFilterValue = { period: 'month' }

// Build the query params object consumed by the GS wallet hooks. For non
// custom-range periods only `period` is sent; for custom_range the inclusive
// start/end bounds are forwarded as RFC3339 timestamps.
export const buildTimeFilterParams = (
  filter: TimeFilterValue
): {
  period?: string
  start?: string
  end?: string
} => {
  if (filter.period === 'custom_range') {
    return { period: filter.period, start: filter.start, end: filter.end }
  }
  return { period: filter.period }
}

// A custom range is valid only when both bounds are present and start <= end.
export const isCustomRangeValid = (filter: TimeFilterValue): boolean => {
  if (filter.period !== 'custom_range') return true
  if (!filter.start || !filter.end) return false
  return new Date(filter.start).getTime() <= new Date(filter.end).getTime()
}

// Map a backend product value to its doctor-facing label.
// Backend values: videoCall | followUp | advice. Aggregate: Total.
export const productLabel = (product: string | undefined, t: (key: string) => string): string => {
  switch (product) {
    case 'videoCall':
    case 'followUp':
    case 'MIA Complete':
      return t('miaComplete')
    case 'advice':
    case 'MIA Basic':
      return t('miaBasic')
    case 'Total':
      return 'Total'
    default:
      return product ?? t('none')
  }
}

// Format an integer-cents amount in its originating currency. Falls back to a
// plain numeric format when the currency code is unknown/invalid so rendering
// never throws.
export const formatCents = (
  cents: number | undefined | null,
  currency: string | undefined | null
): string => {
  const value = (cents ?? 0) / 100
  const code = (currency ?? '').toUpperCase()
  if (code) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code,
      }).format(value)
    } catch {
      // Unknown currency code: fall through to a plain formatted number + code.
    }
  }
  return `${new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}${code ? ` ${code}` : ''}`
}

// Format the optional average/consult duration. The backend returns null/empty
// for products that do not report a duration (Requirements 7.3 / 8.5).
export const formatDuration = (minutes: number | null | undefined, none: string): string => {
  if (minutes === null || minutes === undefined) return none
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(minutes)
}

// Format a consult date (RFC3339 / ISO string) as e.g. "05 Mar 2024".
export const formatConsultDate = (iso: string | undefined, none: string): string => {
  if (!iso) return none
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return none
  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(d)
}

// Format a year/month pair (1-based month) as e.g. "March 2024".
export const formatInvoiceMonth = (year: number | undefined, month: number | undefined): string => {
  if (!year || !month) return '—'
  // month is 1-based from the backend; Date expects 0-based.
  const date = new Date(Date.UTC(year, month - 1, 1))
  return new Intl.DateTimeFormat(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}
